import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Phone, Bot, User, Clock, Loader2, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useCalls, type CallSummary } from '../../hooks/useCalls';
import { cn } from '../../lib/utils';

function useCallsBasePath(): string {
  const { uid } = useParams<{ uid?: string }>();
  return uid ? `/admin/users/${uid}/settings/calls` : '/settings/calls';
}

export function CallsListPage() {
  const [provider, setProvider] = React.useState<'all' | 'twilio' | 'telnyx'>('all');
  const calls = useCalls({
    limit: 100,
    provider: provider === 'all' ? undefined : provider,
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <Select value={provider} onValueChange={(v) => setProvider(v as typeof provider)}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Anbieter</SelectItem>
            <SelectItem value="twilio">Twilio</SelectItem>
            <SelectItem value="telnyx">Telnyx</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {calls.loading ? (
        <div className="flex h-32 items-center justify-center text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade Anrufe …
        </div>
      ) : calls.items.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Noch keine Anrufe aufgezeichnet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {calls.items.map((c) => (
            <CallRow key={c.id} c={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function CallRow({ c }: { c: CallSummary }) {
  const start = c.startedAt?.toDate?.();
  const duration = c.durationSec ?? 0;
  const inProgress = c.outcome === 'in-progress';
  const base = useCallsBasePath();
  return (
    <Link
      to={`${base}/${c.id}`}
      className={cn(
        'group flex items-center gap-4 rounded-md border border-border bg-card px-4 py-3 transition-colors hover:bg-secondary',
        inProgress && 'border-emerald-500/40 bg-emerald-500/5',
      )}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
        <Phone className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{c.callerNumber || 'Unbekannt'}</span>
          <Badge>{c.provider ?? 'twilio'}</Badge>
          {inProgress ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-emerald-400">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" /> live
            </span>
          ) : (
            <Badge variant="muted">{c.outcome ?? '—'}</Badge>
          )}
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          {start ? start.toLocaleString('de-DE') : '—'} · {duration}s
          {c.userTurnCount != null && (
            <>
              {' · '}
              <span className="inline-flex items-center gap-1">
                <User className="h-3 w-3" /> {c.userTurnCount}
              </span>{' '}
              <span className="inline-flex items-center gap-1">
                <Bot className="h-3 w-3" /> {c.assistantTurnCount ?? 0}
              </span>
            </>
          )}
          {(c.latencyP50 ?? 0) > 0 && (
            <>
              {' · '}
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> {c.latencyP50}ms p50
              </span>
            </>
          )}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </Link>
  );
}

function Badge({
  children,
  variant = 'primary',
}: {
  children: React.ReactNode;
  variant?: 'primary' | 'muted';
}) {
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider',
        variant === 'primary' ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
      )}
    >
      {children}
    </span>
  );
}
